(() => {
  const elementName = 'loox-five-star-summary';
  const summaryRequests = new Map();
  const endpointPaths = [
    'product-reviews/bottomline',
    'store-reviews/bottomline'
  ];

  function readFiveStarCount(payload) {
    const rawValue = payload?.ratingDistribution?.['5'];

    if (
      (typeof rawValue !== 'number' && typeof rawValue !== 'string') ||
      (typeof rawValue === 'string' && rawValue.trim() === '')
    ) {
      throw new TypeError('Loox five-star count is missing.');
    }

    const count = Number(rawValue);

    if (!Number.isSafeInteger(count) || count < 0) {
      throw new TypeError('Loox five-star count is invalid.');
    }

    return count;
  }

  async function fetchBottomline(url, signal) {
    const response = await fetch(url, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal
    });

    if (!response.ok) {
      throw new Error(`Loox request failed with status ${response.status}.`);
    }

    return response.json();
  }

  function getCombinedFiveStarCount(publicStoreId) {
    if (summaryRequests.has(publicStoreId)) {
      return summaryRequests.get(publicStoreId);
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);
    const encodedStoreId = encodeURIComponent(publicStoreId);
    const baseUrl = `https://storefront-api.loox.io/storefront/v1/store/${encodedStoreId}/`;

    let request = Promise.all(
      endpointPaths.map((path) => fetchBottomline(`${baseUrl}${path}`, controller.signal))
    )
      .then((bottomlines) => bottomlines.reduce(
        (total, bottomline) => total + readFiveStarCount(bottomline),
        0
      ))
      .finally(() => window.clearTimeout(timeoutId));

    request = request.catch((error) => {
      summaryRequests.delete(publicStoreId);
      throw error;
    });

    summaryRequests.set(publicStoreId, request);
    return request;
  }

  class LooxFiveStarSummary extends HTMLElement {
    connectedCallback() {
      const publicStoreId = this.dataset.publicStoreId?.trim();
      const resolved = this.querySelector('[data-loox-five-star-resolved]');
      const fallback = this.querySelector('[data-loox-five-star-fallback]');
      const label = this.querySelector('[data-loox-five-star-label]');

      if (!publicStoreId || !resolved || !label) {
        return;
      }

      const requestToken = {};
      this.looxRequestToken = requestToken;
      this.dataset.state = 'loading';

      getCombinedFiveStarCount(publicStoreId)
        .then((count) => {
          if (!this.isConnected || this.looxRequestToken !== requestToken) {
            return;
          }

          const formattedCount = new Intl.NumberFormat(
            document.documentElement.lang || 'en-AU'
          ).format(count);
          const reviewNoun = count === 1 ? 'review' : 'reviews';
          const text = `${formattedCount} 5-star ${reviewNoun}`;

          label.textContent = text;
          resolved.setAttribute('aria-label', text);
          resolved.hidden = false;

          if (fallback) {
            fallback.hidden = true;
          }

          this.dataset.state = 'ready';
        })
        .catch(() => {
          if (!this.isConnected || this.looxRequestToken !== requestToken) {
            return;
          }

          resolved.hidden = true;

          if (fallback) {
            fallback.hidden = false;
          }

          this.dataset.state = 'fallback';
        });
    }

    disconnectedCallback() {
      this.looxRequestToken = null;
    }
  }

  if (!customElements.get(elementName)) {
    customElements.define(elementName, LooxFiveStarSummary);
  }
})();
